import { createContext, useContext, useState, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null)
  const timerRef = useRef(null)

  const showToast = useCallback((msg, type = 'error') => {
    clearTimeout(timerRef.current)
    setToast({ msg, type, id: Date.now() })
    timerRef.current = setTimeout(() => setToast(null), 3000)
  }, [])

  const showError = useCallback((msg) => showToast(msg, 'error'), [showToast])
  const showSuccess = useCallback((msg) => showToast(msg, 'success'), [showToast])

  const colors = {
    error:   { accent: '#ffb59b', border: 'rgba(255,91,91,0.25)', icon: 'error' },
    success: { accent: '#b0ceae', border: 'rgba(176,206,174,0.25)', icon: 'check_circle' },
  }

  return (
    <ToastContext.Provider value={{ showToast, showError, showSuccess }}>
      {children}
      <AnimatePresence>
        {toast && (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="fixed bottom-8 left-1/2 z-[200] flex items-center gap-3"
            style={{
              transform: 'translateX(-50%)',
              background: 'rgba(27,28,27,0.96)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: `1px solid ${colors[toast.type]?.border || colors.error.border}`,
              borderRadius: 99,
              padding: '10px 18px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: '1rem', color: colors[toast.type]?.accent || colors.error.accent, fontVariationSettings: "'FILL' 1" }}
            >
              {colors[toast.type]?.icon || 'info'}
            </span>
            <span className="font-label text-xs font-semibold" style={{ color: colors[toast.type]?.accent || colors.error.accent }}>
              {toast.msg}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)
